/*
 * Copyright(c) 2022 General Electric Company. All rights reserved.
 *
 * The copyright to the computer software herein is the property of
 * General Electric Company. The software may be used and/or copied only
 * with the written permission of General Electric Company or in accordance
 * with the terms and conditions stipulated in the agreement/contract
 * under which the software has been supplied.
 */

import * as moment from 'moment';
import * as _ from 'underscore';
import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AppConfigService } from '@app/common/app-config.service';
import { ModalConfig, ModalRef, ModalService, SystemNotificationType } from '@core/public-api';
import { TranslateService } from '@ngx-translate/core';
import { appConstants } from '../../data-model/app-constants';
import { Subscription } from 'rxjs';
import { Logger } from '@core/lib/services';
import { LookupService } from '../../services/lookup.service';
import { ExamContextService } from '../../services/exam-context.service';
import { PatientContextService } from '../../services/patient-context.service';
import { UtilService } from '../../services/util.service';
import { PatientData, PatientInfo, PatientBannerItem } from '../../data-model/patient.types';
import {
  Department,
  LookupData,
  Physician,
  Priority,
  Procedure,
  OrderingLocation
} from '../../data-model/lookup.types';
import { AuditRecord_Exam } from '../../data-model/proxy.types';
import { ConfirmationActionComponent } from '../common/confirmation-action.component';
import { PatientBannerInfo } from '../../data-model/patient-banner-data';
import { ExamInfo } from '../../data-model/exam-data';
import { UserPrivilegesService } from '../../../../../ng-ui/src/app/common/user-privileges.service';
import { NotifyService } from '../../../../../ng-ui/src/app/common/notify.service';

const log = new Logger('ExamHandlerComponent');

@Component({
  selector: 'app-exam-handler',
  templateUrl: './exam-handler.component.html',
  styleUrls: ['./exam-handler.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExamHandlerComponent implements OnInit, OnDestroy {
  // Property
  private subscriptions: Array<Subscription> = [];
  patientData: PatientInfo;
  examData: ExamInfo;
  isLoading: boolean;
  loadError: boolean;
  notificationBanner: boolean;
  isAccessionNumberEditable: boolean;
  isErrorPopUp = false;
  scheduledDateInEditing = false;
  studyDateInEditing = false;

  examHandlerForm: FormGroup;
  patientInfo: PatientBannerInfo;
  isPatientIconHidden = true;
  isPatientDropdownHidden = true;
  isPatientBannerCompressed = true;

  dateFormat: string;
  dateDisplayFormat: string;
  dateFormatDelimiter: string;
  startTime: moment.Moment = moment('00:00:00', appConstants.format.time);
  endTime: moment.Moment = moment('23:59:59', appConstants.format.time);
  todayEod: moment.Moment = moment().endOf('day');
  dob;
  examId: number;

  priorityList: Priority[] = [];
  procedureDescList: Procedure[] = [];
  orderNumberList: string[] = [];
  performingPhysicianList: Physician[] = [];
  referringPhysicianList: Physician[] = [];
  orderingLocationList: OrderingLocation[] = [];
  departmentsList: Department[] = [];
  originalFormValue: any;
  internalExamId: number;
  confirmationMessage: string;

  private auditComments = [];

  // Worklist notification object.
  notifications = {
    a_errorGeneral: {
      id: 'errorGeneral',
      label: this.translate.instant('msg.error.general'),
      show: false,
      hideClose: true,
      type: SystemNotificationType.highSeverityPrimary,
      multiLineLayout: false
    },
    b_errorLoading: {
      id: 'errorLoading',
      label: this.translate.instant('msg.exam.error.loading'),
      show: false,
      hideClose: true,
      type: SystemNotificationType.highSeverityPrimary,
      multiLineLayout: false
    },
    c_successUpdate: {
      id: 'successExamUpdated',
      label: this.translate.instant('msg.exam.success.examUpdated'),
      show: false,
      hideClose: true,
      type: SystemNotificationType.success,
      multiLineLayout: false
    },
    d_errorUpdate: {
      id: 'errorExamUpdate',
      label: this.translate.instant('msg.exam.error.examUpdated'),
      show: false,
      hideClose: true,
      type: SystemNotificationType.highSeverityPrimary,
      multiLineLayout: false
    },
    e_warningTimeout: {
      id: 'warningExamUpdateTimeout',
      label: this.translate.instant('msg.exam.error.examUpdated.exceeded.time'),
      show: false,
      hideClose: true,
      type: SystemNotificationType.moderateSeverityPrimary,
      multiLineLayout: false
    },
    f_errorAccessionAlreadyExists: {
      id: 'accessionAlreadyExists',
      label: this.translate.instant('msg.exam.error.accessionNumber.already.exists'),
      show: false,
      hideClose: true,
      type: SystemNotificationType.highSeverityPrimary,
      multiLineLayout: false
    },
    g_errorAccessionUpdatingNull: {
      id: 'errorAccessionUpdatingNull',
      label: this.translate.instant('msg.exam.error.invalid.accession.number'),
      show: false,
      hideClose: true,
      type: SystemNotificationType.highSeverityPrimary,
      multiLineLayout: false
    },
    h_warningExamNotInArrived: {
      id: 'warningExamNotInArrived',
      label: this.translate.instant('msg.exam.error.InvalidExamStatusMessage'),
      show: false,
      hideClose: true,
      type: SystemNotificationType.infoGeneral,
      multiLineLayout: false
    }
  };
  // Constructor
  constructor(
    public config: ModalConfig,
    private modalRef: ModalRef,
    private cd: ChangeDetectorRef,
    private fb: FormBuilder,
    private translate: TranslateService,
    private appConfigService: AppConfigService,
    private lookupService: LookupService,
    private examContextService: ExamContextService,
    private patientContextService: PatientContextService,
    private modalService: ModalService,
    private utilService: UtilService,
    private userPrivilegeService: UserPrivilegesService,
    private notify: NotifyService
  ) {
    const data = this.config?.data;
    if (data?.examId) {
      this.examId = data?.examId;
      this.examContextService.initialize(data?.examId);
    }
    if (data?.patientId) {
      this.patientContextService.initialize(data?.patientId);
    }
  }

  ngOnInit(): void {
    this.config.headerContent = this.translate.instant('label.editExam');
    this.setDateFormat();
    this.subscribeDateFormat();
    this.buildForm();
    this.initFormData();
  }

  // Form
  buildForm() {
    this.examHandlerForm = this.fb.group({
      scheduledDate: ['', [Validators.required]],
      scheduledTime: ['', [Validators.required]],
      studyDate: ['', [Validators.required]],
      studyTime: ['', [Validators.required]],
      priority: [''],
      orderNumber: [''],
      accessionNumber: ['', [Validators.pattern(appConstants.regEx.space)]],
      referringPhysician: [''],
      performingPhysician: [''],
      procedureDescription: ['', [Validators.required]],
      procedureCode: [''],
      clinicalComments: ['', [this.utilService.sqlInjectorCharValidator()]],
      orderingLocation: [''],
      departmentName: ['']
    });
  }

  //initialize form data
  async initFormData() {
    this.isLoading = true;
    this.loadError = false;
    this.notificationBanner = false;
    if (await this.isValidExamSatus()) {
      this.subscriptions.push(
        this.lookupService.getLookUpData().subscribe((data: LookupData) => {
          if (data.error) {
            this.loadingFailed(data.error);
          } else {
            this.populateList(data);
            this.loadFormData();
          }
        })
      );
    } else {
      this.examStatusError();
    }
  }

  subscribeDateFormat() {
    this.subscriptions.push(
      this.appConfigService.getDateTimeObserver().subscribe(() => {
        this.setDateFormat();
      })
    );
  }

  private setDateFormat() {
    const dateTimePref = this.appConfigService.getDateTimePref();
    if (dateTimePref) {
      this.dateFormat = dateTimePref.inputFormat;
      this.dateDisplayFormat = dateTimePref.dateFormat;
      this.dateFormatDelimiter = dateTimePref.delimiter;
    }
  }

  loadFormData() {
    this.subscriptions.push(
      this.examContextService.getExamData().subscribe(examData => {
        if (Object.entries(examData).length > 1) {
          if (examData?.error === undefined) {
            this.setExamContext(examData);

            this.subscriptions.push(
              this.patientContextService.getPatientData().subscribe(patientDetail => {
                if (Object.entries(patientDetail).length > 1) {
                  if (patientDetail?.error === undefined) {
                    this.setPatientContext(patientDetail);
                    this.isLoading = false;
                    this.cd.detectChanges();
                  } else {
                    this.loadingFailed();
                  }
                }
              })
            );
          } else {
            this.loadingFailed();
          }
        }
      })
    );
  }

  // Populate dropdown list
  populateList(lookUpData) {
    const lookupFields = appConstants.lookup;
    Object.keys(lookUpData).forEach(field => {
      switch (field) {
        case lookupFields.PRIORITIES:
          this.priorityList = lookUpData[field];
          break;
        case lookupFields.PROCEDURES:
          this.procedureDescList = lookUpData[field];
          break;
        case lookupFields.PHYSICIANS:
          this.performingPhysicianList = lookUpData[field].filter(ele => ele.active == true || ele.internalStid == 0);
          this.referringPhysicianList = lookUpData[field].filter(ele => ele.active == true || ele.internalStid == 0);
          break;
        case lookupFields.ORDERING_LOCATIONS:
          this.orderingLocationList = lookUpData[field];
          break;
        case lookupFields.DEPARTMENTS:
          this.departmentsList = lookUpData[field];
          break;
        default:
          log.debug('RM Exam Component Lookup UNKOWN', field);
          break;
      }
    });
  }

  private isValidExamSatus(): Promise<boolean> {
    return new Promise<any>(resolve => {
      this.examContextService
        .fetchExamStatus(this.examId)
        .then(data => {
          log.debug('RM Exam Service - Fetch Status complete', data);
          if (data?.requestedProcedure?.statusId < 50) {
            resolve(true);
          } else {
            resolve(false);
          }
        })
        .catch(ex => {
          log.debug('RM Exam Service - Fetch Status failed', ex);
          resolve(false);
        });
    });
  }

  onInput() {
    this.isErrorPopUp = false;
    this.removeNotificationBanner();
    this.validateAccessionNumber();
  }

  // Set Exam context
  private setExamContext(examData) {
    try {
      this.examData = examData;
      this.isAccessionNumberEditable =
        this.userPrivilegeService.getUserPrivileges().indexOf('PrivilegedModifyExam') > -1;
      if (!this.isAccessionNumberEditable) {
        this.examHandlerForm.controls['accessionNumber'].clearValidators();
      }
      const examInfoFields = appConstants.examInfo;
      Object.keys(examData).forEach(field => {
        if (examData[field] != null) {
          switch (field) {
            case examInfoFields.SCHEDULED_DATE_TIME:
              const scheduledDate = moment(examData[field], appConstants.format.dateTime);
              this.setUserSelection(examInfoFields.SCHEDULED_DATE, scheduledDate);
              const scheduledTime = moment(
                scheduledDate.hours() + ':' + scheduledDate.minutes(),
                appConstants.format.time
              );
              this.setUserSelection(examInfoFields.SCHEDULED_TIME, scheduledTime);
              break;
            case examInfoFields.STUDY_DATE_TIME:
              const studyDate = moment(examData[field], appConstants.format.dateTime);
              this.setUserSelection('studyDate', studyDate);
              const studyTime = moment(studyDate.hours() + ':' + studyDate.minutes(), appConstants.format.time);
              this.setUserSelection('studyTime', studyTime);
              break;
            case examInfoFields.PRIORITY:
              const priority = this.lookupService.findPrioritiesById(examData[field]);
              this.setUserSelection('priority', priority);
              break;
            case examInfoFields.ORDER_NUMBER:
              this.setUserSelection('orderNumber', examData[field]);
              break;
            case examInfoFields.ACCESSION_NUMBER:
              this.setUserSelection('accessionNumber', examData[field]);
              break;
            case examInfoFields.REFERRING_PHYSICIAN:
              const referringPhysician = this.lookupService.findPhysiciansById(examData[field]);
              this.setUserSelection('referringPhysician', referringPhysician);
              break;
            case examInfoFields.PROCEDURE_CKEY:
              const procedure = this.lookupService.findProceduresById(examData[field]);
              this.setUserSelection('procedureCode', procedure.procedureCode);
              this.setUserSelection('procedureDescription', procedure);
              break;
            case examInfoFields.CLINICAL_COMMENTS:
              this.setUserSelection('clinicalComments', examData[field]);
              break;
            case examInfoFields.PERFORMING_PHYSICIAN:
              const performingPhysician = this.lookupService.findPhysiciansById(examData[field]);
              this.setUserSelection('performingPhysician', performingPhysician);
              break;
            case examInfoFields.DEPARTMENT_NAME:
              const department = this.lookupService.findDepartmentById(examData[field]);
              this.setUserSelection('departmentName', department);
              break;
            case examInfoFields.ORDERING_LOCATION:
              const orderingLocation = this.lookupService.findOrderingLocationsById(examData[field]);
              this.setUserSelection('orderingLocation', orderingLocation);
              break;
            case examInfoFields.INTERNAL_EXAM_ID:
              this.internalExamId = examData[field];
              break;
            default:
              log.debug('RecordMgmt Exam Component - Ignore edit exam field', field);
              break;
          }
        }
      });
      this.originalFormValue = { ...this.examHandlerForm.value };
      log.debug('RecordMgmt Exam Component - edit exam form details added', this.examHandlerForm);
    } catch (err) {
      this.loadingFailed(err);
    }
  }

  // Populate patient info on patient banner
  // Fill ordernumber list to display on dropdown
  private setPatientContext(data: PatientData) {
    try {
      this.orderNumberList = data.patientOrders;
      const defaultDomain = this.appConfigService.getUserDefaultDomain();
      const defaultAuthority = this.appConfigService.getDomainSysCode(defaultDomain);
      if (data.patientInfo) {
        this.patientData = data.patientInfo;
        this.dob = this.patientData.dob ? moment(this.patientData.dob) : undefined;
        const patient = _.find(this.patientData?.identifier, el => el.assigningAuthorityName == defaultAuthority);
        const bannerInfo: PatientBannerItem[] = [
          {
            key: this.translate.instant('label.patientId'),
            value: patient == undefined ? this.translate.instant('label.unavailable') : patient.patientId
          },
          {
            key: this.translate.instant('label.gender'),
            value: this.lookupService.findGenderById(this.patientData.gender).desc
          },
          { key: this.translate.instant('label.age'), value: this.patientData.age },
          {
            key: this.translate.instant('label.dob'),
            value: this.patientData.dob
              ? moment(this.patientData.dob, appConstants.format.dob).format(this.dateDisplayFormat)
              : ''
          }
        ];

        this.patientInfo = new PatientBannerInfo(this.patientData.patientName, this.patientData.iPatName, bannerInfo);
      }
    } catch (err) {
      this.loadingFailed(err);
    }
  }

  get formControls() {
    return this.examHandlerForm.controls;
  }

  //set user selection to form fields
  setUserSelection(field: string, selectedItem: any) {
    if (this.notificationBanner) this.removeNotificationBanner();
    this.resetDateInEditingFlags();
    this.isErrorPopUp = false;
    this.examHandlerForm.get(field).setValue(selectedItem);
    if (this.originalFormValue) {
      if (JSON.stringify(this.originalFormValue) === JSON.stringify(this.examHandlerForm.value)) {
        this.examHandlerForm.markAsPristine();
      } else {
        this.examHandlerForm.markAsDirty();
      }
    }
  }

  //populate procedure code for selected procedure description
  updateProcedureSelection(procedure) {
    this.setUserSelection(appConstants.examInfo.PROCEDURE_DESCRIPTION, procedure);
    this.setUserSelection(appConstants.examInfo.PROCEDURE_CODE, procedure.procedureCode);
  }

  // Function to generate audit information
  private generateAuditInfo(origInfo: ExamInfo, newInfo: ExamInfo): AuditRecord_Exam {
    this.auditComments.push(appConstants.auditLabel.commentHead);

    const examLabel = appConstants.examInfo;
    Object.keys(newInfo).forEach(field => {
      if (newInfo[field] !== origInfo[field]) {
        if (field === examLabel.PRIORITY) this.appendComments(examLabel.LABEL_PRIORITY, 'priority', 'priorityDesc');
        if (field === examLabel.ORDER_NUMBER) this.appendComments(examLabel.LABEL_ORDER_NUMBER, 'orderNumber');
        if (field === examLabel.CLINICAL_COMMENTS)
          this.appendComments(examLabel.LABEL_CLINICAL_COMMENTS, 'clinicalComments');
        if (field === examLabel.ACCESSION_NUMBER) this.appendComments(examLabel.ACCESSION_NUMBER, 'accessionNumber');
        if (field === examLabel.DEPARTMENT_NAME)
          this.appendComments(examLabel.LABEL_DEPARTMENT_NAME, 'departmentName', 'departmentName');
        if (field === examLabel.STUDY_DATE_TIME && newInfo[field] != origInfo[examLabel.STUDY_DATE_TIME])
          this.appendComments(
            examLabel.STUDY_DATE_TIME,
            undefined,
            undefined,
            origInfo[examLabel.STUDY_DATE_TIME],
            newInfo[field]
          );
        if (field === examLabel.SCHEDULED_DATE_TIME && newInfo[field] != origInfo[examLabel.SCHEDULED_DATE_TIME])
          this.appendComments(
            examLabel.SCHEDULED_DATE_TIME,
            undefined,
            undefined,
            origInfo[examLabel.SCHEDULED_DATE_TIME],
            newInfo[field]
          );
        if (field === examLabel.PERFORMING_PHYSICIAN)
          this.appendComments(examLabel.LABEL_PERFORMING_PHYSICIAN, 'performingPhysician', 'formalName');
        if (field === examLabel.REFERRING_PHYSICIAN)
          this.appendComments(examLabel.LABEL_REFERRING_PHYSICIAN, 'referringPhysician', 'formalName');
        if (field === examLabel.ORDERING_LOCATION)
          this.appendComments(examLabel.LABEL_ORDERING_LOCATION, 'orderingLocation', 'orderingLocationId');
        if (field === examLabel.PROCEDURE_CKEY) {
          this.appendComments(examLabel.LABEL_PROCEDURE_CODE, 'procedureCode');
          this.appendComments(examLabel.LABEL_PROCEDURE_NAME, 'procedureDescription', 'procedureDesc');
        }
      }
    });

    return {
      actionType: examLabel.ACTION_TYPE,
      patientIdentifier: this.patientData.patientName,
      patientCkey: this.patientData.internalPatientId,
      examIdentifier: this.examData.accessionNumber,
      examCkey: this.examData.internalExamId,
      comments: this.auditComments.join('')
    };
  }
  private appendComments(fieldLabel: string, field: string, target?: string, fromData?: string, toData?: string): void {
    if (fromData === undefined) {
      fromData =
        target && this.originalFormValue[field] ? this.originalFormValue[field][target] : this.originalFormValue[field];
    }
    if (toData === undefined) {
      toData =
        target && this.examHandlerForm.get(field).value
          ? this.examHandlerForm.get(field).value[target]
          : this.examHandlerForm.get(field).value;
    }

    this.auditComments.push(
      [
        fieldLabel,
        appConstants.auditLabel.from,
        fromData,
        appConstants.auditLabel.to,
        toData,
        appConstants.auditLabel.end
      ].join('')
    );
  }

  // Update Exam information confirmation modal
  confirmExamUpdate() {
    if (this.notificationBanner) this.removeNotificationBanner();
    this.examHandlerForm.value.accessionNumber = this.examHandlerForm.value.accessionNumber.trim();
    const originalExamInfo = new ExamInfo(this.originalFormValue, this.internalExamId);
    const updatedExamInfo = new ExamInfo(this.examHandlerForm.value, this.internalExamId);
    const auditRecord: AuditRecord_Exam = this.generateAuditInfo(originalExamInfo, updatedExamInfo);
    if (this.originalFormValue.accessionNumber.length > 0 && this.examHandlerForm.value.accessionNumber.length == 0) {
      this.displayNotificationBanner(this.notifications.g_errorAccessionUpdatingNull);
      this.isErrorPopUp = true;
    } else {
      if (this.examHandlerForm.value.accessionNumber.length == 0) {
        delete updatedExamInfo.accessionNumber;
      }
      this.openConfirmationModal(updatedExamInfo, auditRecord);
    }
  }

  openConfirmationModal(saveExamPayload: ExamInfo, auditRecord: AuditRecord_Exam) {
    // Show confirmation modal
    saveExamPayload.userName = this.appConfigService.getUserId();
    if (this.appConfigService.getAppConfigByKey('ISF.StudyManagement.AllowCreateRISOrderInfo') == 'true') {
      this.confirmationMessage = this.translate.instant('modal.exam.confirmation.examUpdate.text');
    } else {
      this.confirmationMessage = this.translate.instant('modal.exam.risConfirmation.examUpdate.text');
    }
    this.modalService.openModal(ConfirmationActionComponent, {
      isBlockModal: true,
      disableHeaderCloseBtn: true,
      modalClass: 'modal--medium',
      data: {
        headerNotificationText: 'modal.exam.confirmation.examUpdate',
        headerNotificationType: SystemNotificationType.highSeverityPrimary,
        message: this.confirmationMessage,
        noButtonLabel: this.translate.instant('button.cancel'),
        yesButtonLabel: this.translate.instant('button.submit'),
        callBack: () => this.updateExamInfo(saveExamPayload, auditRecord)
      }
    });
  }

  updateExamInfo(saveExamPayload: ExamInfo, auditRecord: AuditRecord_Exam) {
    return this.examContextService
      .updateExam(saveExamPayload, auditRecord)
      .then(() => {
        this.displayNotificationBanner(this.notifications.c_successUpdate);
        this.originalFormValue = { ...this.examHandlerForm.value };
        this.examHandlerForm.markAsPristine();
        this.cd.detectChanges();
      })
      .catch(ex => {
        log.debug('RM Exam Component : update exam failed', ex);
        if (ex.status == appConstants.appURLs.timeoutStatus) {
          this.displayNotificationBanner(this.notifications.e_warningTimeout);
          this.examHandlerForm.disable();
        } else if (ex.status == appConstants.appURLs.unprocessableEntity) {
          this.displayNotificationBanner(this.notifications.f_errorAccessionAlreadyExists);
          this.isErrorPopUp = true;
        } else if (ex.status === 501) {
          this.notify.critical(
            '<h3>' +
              this.translate.instant('studyManagement.purgeFailureTitle') +
              '</h3><div>' +
              this.translate.instant('studyManagement.purgeFailureMessage') +
              '</div><br/>',
            this.translate.instant('editExam.label')
          );
        } else if (ex.status == appConstants.appURLs.partialStatus) {
          this.updatePartialFailureHandler();
        } else {
          this.displayNotificationBanner(this.notifications.d_errorUpdate);
        }
        this.cd.detectChanges();
        if (ex.status == appConstants.appURLs.conflictStatus) {
          this.displayConflictExamDataErrorModal();
        }
      });
  }

  private displayConflictExamDataErrorModal() {
    // Function to handle old patient data update
    const sub = this.modalService
      .openModal(this.translate.instant('modal.exam.error.conflict.examUpdate.text'), {
        headerContent: this.translate.instant('modal.exam.error.conflict.examUpdate'),
        disableHeaderCloseBtn: true,
        enableYesBtn: false,
        isDecisionModal: false,
        noButtonLabel: this.translate.instant('button.ok')
      })
      .afterClosed.subscribe(() => {
        sub.unsubscribe();
        this.ngOnInit();
      });
  }

  // Function to handle partial exam update
  private updatePartialFailureHandler() {
    this.notifications.d_errorUpdate.show = true;
    this.cd.detectChanges();
    const sub = this.modalService
      .openModal(this.translate.instant('msg.error.partialUpdate'), {
        headerContent: this.translate.instant('msg.record.management.text'),
        disableHeaderCloseBtn: true,
        enableYesBtn: false,
        isDecisionModal: false,
        noButtonLabel: this.translate.instant('button.ok')
      })
      .afterClosed.subscribe(() => {
        sub.unsubscribe();
        this.ngOnInit();
      });
  }
  displayNotificationBanner(notification) {
    notification.show = true;
    this.notificationBanner = true;
  }

  // Handler - Loading failed
  loadingFailed(err?) {
    this.isLoading = false;
    this.loadError = true;
    this.displayNotificationBanner(this.notifications.b_errorLoading);
    this.cd.detectChanges();
    log.debug('RM Exam Component loading failed', err);
  }

  // Handler - not valid exam status
  examStatusError() {
    this.isLoading = false;
    this.loadError = true;
    this.displayNotificationBanner(this.notifications.h_warningExamNotInArrived);
    this.cd.detectChanges();
  }

  // Checking the Date values to show Date required error
  dateBoxvalueChanged(field: string) {
    const value = this.examHandlerForm.get(field).value;
    switch (field) {
      case 'scheduledDate':
        this.scheduledDateInEditing = value === undefined ? true : false;
        break;
      case 'studyDate':
        this.studyDateInEditing = value === undefined ? true : false;
        break;
    }
  }

  validateAccessionNumber() {
    if (this.originalFormValue.accessionNumber.length > 0 && this.examHandlerForm.value.accessionNumber.length == 0) {
      this.formControls.accessionNumber.setErrors({ empty: true });
    }
  }

  // Reseting the Date Editing flags
  resetDateInEditingFlags() {
    this.scheduledDateInEditing = false;
    this.studyDateInEditing = false;
  }

  //remove all the notification banner from Edit exam modal
  removeNotificationBanner() {
    this.notificationBanner = false;
    this.notifications.a_errorGeneral.show = false;
    this.notifications.b_errorLoading.show = false;
    this.notifications.c_successUpdate.show = false;
    this.notifications.d_errorUpdate.show = false;
    this.notifications.e_warningTimeout.show = false;
    this.notifications.f_errorAccessionAlreadyExists.show = false;
    this.notifications.g_errorAccessionUpdatingNull.show = false;
  }

  /**
   * Reset the form to original values
   */
  resetForm() {
    this.removeNotificationBanner();
    this.examHandlerForm.patchValue(this.originalFormValue);
    this.examHandlerForm.markAsPristine();
    this.cd.detectChanges();
  }

  // Cancel Update action
  closeModal(viewInDxHub: boolean) {
    this.modalRef.close(viewInDxHub);
  }

  ngOnDestroy(): void {
    this.examContextService.setExamData({});
    this.patientContextService.setPatientData({});
    this.subscriptions.forEach(subscriber => {
      subscriber.unsubscribe();
    });
  }
}
